"use client";
import { redirect } from "next/navigation";
import { createContext, useContext, useState } from "react";

type User = {
	username: string;
	email?: string;
	mobile?: string;
	cutScore?: number;
};

type AuthContextType = {
	user: User | null;
	loading: boolean;
	refreshUser: () => Promise<void>;
	setUser: React.Dispatch<React.SetStateAction<User | null>>;
	logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType>({
	user: null,
	loading: true,
	refreshUser: async () => {},
	setUser: () => {},
	logout: async () => {},
});

export const AuthProvider = ({
	children,
	initialUser,
}: {
	children: React.ReactNode;
	initialUser: User;
}) => {
	const [user, setUser] = useState<User | null>(initialUser);
	const [loading, setLoading] = useState(false);

	const refreshUser = async () => {
		setLoading(true);
		try {
			const res = await fetch("/api/auth/me", {
				cache: "no-store",
				credentials: "same-origin",
			});
			if (res.ok) {
				const data = await res.json();
				setUser(data);
			} else {
				setUser(null);
			}
		} catch {
			setUser(null);
		} finally {
			setLoading(false);
		}
	};

	const logout = async () => {
		await fetch("/api/auth/logout", { method: "POST" });
		setUser(null);
		redirect("/");
	};

	return (
		<AuthContext.Provider
			value={{ user, loading, refreshUser, setUser, logout }}
		>
			{children}
		</AuthContext.Provider>
	);
};

export const useAuth = () => useContext(AuthContext);
